import { useMemo, useCallback, useEffect } from "react";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { PhantomWalletAdapter, SolflareWalletAdapter } from "@solana/wallet-adapter-wallets";
import { WalletError, WalletNotReadyError } from "@solana/wallet-adapter-base";
import "@solana/wallet-adapter-react-ui/styles.css";

export const SolanaProvider = ({ children }: { children: React.ReactNode }) => {
  // RPC endpoint - usa env var se disponibile, altrimenti pubblico
  const endpoint = useMemo(() => {
    const FALLBACK_RPC = "https://rpc.ankr.com/solana";
    try {
      const envRpc = import.meta.env.VITE_SOLANA_RPC;
      if (envRpc && typeof envRpc === "string" && (envRpc.startsWith("http://") || envRpc.startsWith("https://"))) {
        return envRpc.trim();
      }
    } catch (e) {
      console.warn("[SolanaProvider] Failed to read VITE_SOLANA_RPC, using fallback");
    }
    return FALLBACK_RPC;
  }, []);

  console.log("🟢 SolanaProvider using endpoint:", endpoint);

  // Wallet adapters espliciti come da documentazione 2025
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ],
    []
  );

  // Error handler per wallet errors
  const onError = useCallback((error: WalletError) => {
    console.error("[Wallet Error]", error.name, error.message);
    
    // Log specifico per debug
    if (error instanceof WalletNotReadyError) {
      console.warn("[Wallet] Wallet not ready - user may need to approve connection in wallet app");
    }
    
    // Non mostrare toast per errori comuni come "User rejected"
    if (error.message?.includes("User rejected") || 
        error.message?.includes("cancelled") ||
        error.message?.includes("denied")) {
      console.log("[Wallet] User cancelled connection");
      return;
    }
  }, []);

  // Debug: log wallet detection on mount and periodically
  useEffect(() => {
    const logWalletState = () => {
      const w = window as any;
      console.log("[Wallet Detection]", {
        phantom: !!w.phantom?.solana,
        solflare: !!w.solflare,
        solflareConnected: w.solflare?.isConnected,
        solflarePublicKey: w.solflare?.publicKey?.toString()?.slice(0, 8),
        solana: !!w.solana,
        isPhantom: w.solana?.isPhantom,
        isSolflare: w.solflare?.isSolflare,
      });
    };
    
    logWalletState();
    
    // Re-check after a delay (for Solflare in-app browser)
    const timeoutId = setTimeout(logWalletState, 2000);
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider 
        wallets={wallets} 
        autoConnect
        onError={onError}
      >
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};
